import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import ShareIcon from '@mui/icons-material/Share';
import CheckIcon from '@mui/icons-material/Check';
import Button from '@mui/material/Button';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UserAvatar from './user-avatar/UserAvatar';
import Credits from './credits/Credits';
import { useAuth } from '../../../helper/hooks/useAuth';
import Download from './download/Download';
import { useState } from 'react';
import UpgradeModal from '../../../shared/upgrade-modal/UpgradeModal';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

const NavRight = ({
  isMobile,
  isTablet,
  isProjectRoute,
  projectId,
  hasVideo,
  videoUrl,
  updating,
  assetsLoaded,
  version,
  exportStatus,
  progress,
  isTranslating,
  canExport,
  projectName,
  handleGenerate,
  handleUpdate,
  handleShare
}) => {
  const [openUpgradeModal, setOpenUpgradeModal] = useState(false);

  const isProcessing = exportStatus === 'INITIATED';
  const isProjectPage = isProjectRoute && projectId;
  const { user, isFreePlan, credits } = useAuth();

  const isBlocked = updating || isProcessing || !assetsLoaded || isTranslating || !canExport;
  const isUpdating = updating || isProcessing;
  const showUpdateButton = version >= 2;

  const handleUpdateClick = isBlocked
    ? undefined
    : isFreePlan
      ? () => setOpenUpgradeModal(true)
      : handleUpdate;

  const updateLabel = isUpdating ? `Updating... ${progress}%` : 'Update Video';

  const updateButtonIcon = isUpdating ? (
    <CircularProgress
      size={isMobile ? 16 : isTablet ? 20 : 24}
      sx={{ color: 'var(--btn-purple-color)' }}
    />
  ) : isFreePlan ? (
    <LockOutlinedIcon fontSize={isMobile ? 'small' : 'medium'} />
  ) : (
    <CheckIcon fontSize={isMobile ? 'small' : 'medium'} />
  );

  const shimmerEffectBox = {
    position: 'relative',
    overflow: 'hidden',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '8px',
      background: `linear-gradient(
        90deg,
        transparent 0%,
        var(--theme-color) 50%,
        transparent 100%
      )`,
      backgroundSize: '200% 100%',
      animation: 'shimmer 1.5s infinite linear',
      pointerEvents: 'none',
      opacity: 0.3,
      zIndex: 999
    }
  };

  return (
    <div className="nav-right">
      {isProjectPage &&
        (hasVideo ? (
          <>
            <Download
              videoUrl={videoUrl}
              isMobile={isMobile}
              isTablet={isTablet}
              projectName={projectName}
              projectId={projectId}
            />

            <Box
              sx={{
                display: 'flex',
                background: 'var(--white)',
                height: isMobile ? '32px' : isTablet ? '40px' : '46px',
                borderRadius: '8px',
                justifyContent: 'space-between',
                marginRight: isMobile ? 1 : isTablet ? 1.5 : 2,
                ...(isUpdating ? shimmerEffectBox : {})
              }}>
              <Button
                variant="contained"
                startIcon={<ShareIcon fontSize={isMobile ? 'small' : 'medium'} />}
                onClick={handleShare}
                aria-label="Share project"
                disabled={isBlocked}
                sx={{
                  background: 'var(--btn-purple-color)',
                  color: 'var(--white)',
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontSize: isMobile ? '10px' : isTablet ? '14px' : '16px',
                  whiteSpace: 'nowrap',
                  fontWeight: 600,
                  padding: isMobile ? '4px 8px' : isTablet ? '6px 14px' : '8px 16px',
                  '&:hover': {
                    background: 'var(--btn-purple-color)',
                    opacity: 0.9,
                    boxShadow: '0 6px 16px rgba(91, 76, 255, 0.4)'
                  },
                  '&:disabled': {
                    background: '#ccc',
                    color: '#666'
                  }
                }}>
                Video Link
              </Button>

              {showUpdateButton && (
                <Box
                  onClick={handleUpdateClick}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mr: isMobile ? 1 : isTablet ? 1.5 : 2,
                    ml: isMobile ? 1 : isTablet ? 1.5 : 2,
                    borderRadius: '8px',
                    padding: isMobile ? '4px 6px' : isTablet ? '6px 8px' : '8px 10px',
                    color: isBlocked ? '#666' : 'var(--btn-purple-color)',
                    cursor: isBlocked ? 'not-allowed' : 'pointer',
                    '&:hover': {
                      opacity: isBlocked ? 1 : 0.8
                    }
                  }}>
                  {updateButtonIcon}
                  <Typography
                    sx={{
                      fontSize: isMobile ? '10px' : isTablet ? '14px' : '16px',
                      whiteSpace: 'nowrap',
                      fontWeight: 600,
                      userSelect: 'none'
                    }}>
                    {updateLabel}
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        ) : (
          <Button
            variant="contained"
            startIcon={
              isProcessing ? (
                <CircularProgress size={20} sx={{ color: 'var(--btn-purple-color)' }} />
              ) : (
                <CheckCircleIcon fontSize={isMobile ? 'small' : 'medium'} />
              )
            }
            disabled={isBlocked}
            onClick={handleGenerate}
            sx={{
              background: 'var(--btn-purple-color)',
              color: 'var(--white)',
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: isMobile ? '12px' : isTablet ? '14px' : '15px',
              fontWeight: 600,
              padding: isMobile ? '6px 12px' : isTablet ? '6px 14px' : '8px 16px',
              marginRight: '16px',
              boxShadow: '0 4px 12px rgba(91, 76, 255, 0.3)',
              '&:hover': {
                background: 'var(--btn-purple-color)',
                opacity: 0.9,
                boxShadow: '0 6px 16px rgba(91, 76, 255, 0.4)'
              },
              '&:disabled': {
                background: '#ccc',
                color: '#666'
              },
              ...(isProcessing ? shimmerEffectBox : {})
            }}>
            {isProcessing ? `Exporting... ${progress}%` : 'Export Video'}
          </Button>
        ))}

      {!isProjectPage && <Credits credits={credits} />}

      <UserAvatar user={user} />

      <UpgradeModal
        open={openUpgradeModal}
        onClose={() => setOpenUpgradeModal(false)}
        title="Update Video"
        errorMessage="You need to upgrade your plan to update this video."
        upgradeButtonText="Upgrade"
      />

      <style jsx>{`
        .nav-right {
          display: flex;
          align-items: center;
          gap: 12px;
        }
      `}</style>
    </div>
  );
};

export default NavRight;
